import React, { useState, useEffect } from 'react';
import { api, endpoints } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { ReactComponent as SettingsIcon } from '../../icons/settings.svg';
import '../admin/OrganizationsPage.css'; // Aynı CSS'i kullan

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
  </svg>
);

const WarningIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
  </svg>
);

const LicensesPage = () => {
  const { logout } = useAuth();
  const [licenses, setLicenses] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    organization: '',
    status: 'trial',
    valid_from: new Date().toISOString().split('T')[0],
    valid_until: '',
    auto_renew: false,
    billing_cycle: 'monthly',
    price: 0
  });

  useEffect(() => {
    fetchLicenses();
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const response = await api.get(endpoints.admin.organizations.list);
      const orgsData = response.results || response || [];
      setOrganizations(orgsData);
    } catch (err) {
      console.error('Error fetching organizations:', err);
    }
  };

  const fetchLicenses = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(endpoints.admin.licenses.list);
      const licensesData = response.results || response || [];
      setLicenses(licensesData);
    } catch (err) {
      console.error('Error fetching licenses:', err);
      setError(err.response?.data?.error || 'Lisanslar yüklenemedi');
      if (err.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLicense = async (e) => {
    e.preventDefault();
    try {
      await api.post(endpoints.admin.licenses.list, formData);
      setShowCreateModal(false);
      resetForm();
      fetchLicenses();
    } catch (err) {
      console.error('Error creating license:', err);
      setError(err.response?.data?.error || 'Lisans oluşturulamadı');
    }
  };

  const handleUpdateLicense = async (e) => {
    e.preventDefault();
    try {
      await api.patch(
        endpoints.admin.licenses.detail(selectedLicense.id),
        formData
      );
      setShowEditModal(false);
      resetForm();
      setSelectedLicense(null);
      fetchLicenses();
    } catch (err) {
      console.error('Error updating license:', err);
      setError(err.response?.data?.error || 'Lisans güncellenemedi');
    }
  };

  const handleToggleStatus = async (license) => {
    const newStatus = license.status === 'active' ? 'suspended' : 'active';
    try {
      await api.patch(endpoints.admin.licenses.detail(license.id), {
        status: newStatus
      });
      fetchLicenses();
    } catch (err) {
      console.error('Error toggling license status:', err);
      setError(err.response?.data?.error || 'Durum güncellenemedi');
    }
  };

  const openEditModal = (license) => {
    setSelectedLicense(license);
    setFormData({
      organization: license.organization,
      status: license.status,
      // API returns ISO-8601 datetimes (e.g. "2026-08-20T00:00:00Z"), but
      // <input type="date"> only accepts YYYY-MM-DD and silently renders
      // empty for anything else — looked like the dates had been lost.
      valid_from: license.valid_from ? license.valid_from.split('T')[0] : '',
      valid_until: license.valid_until ? license.valid_until.split('T')[0] : '',
      auto_renew: license.auto_renew,
      billing_cycle: license.billing_cycle,
      price: license.price
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (license) => {
    setSelectedLicense(license);
    setShowDeleteModal(true);
  };

  const handleDeleteLicense = async () => {
    if (!selectedLicense) return;
    try {
      await api.delete(endpoints.admin.licenses.detail(selectedLicense.id));
      setShowDeleteModal(false);
      setSelectedLicense(null);
      fetchLicenses();
    } catch (err) {
      console.error('Error deleting license:', err);
      setError(err.response?.data?.error || 'Lisans silinemedi');
      setShowDeleteModal(false);
    }
  };

  const resetForm = () => {
    setFormData({
      organization: '',
      status: 'trial',
      valid_from: new Date().toISOString().split('T')[0],
      valid_until: '',
      auto_renew: false,
      billing_cycle: 'monthly',
      price: 0
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Statistics
  const stats = {
    total: licenses.length,
    active: licenses.filter(l => l.status === 'active').length,
    trial: licenses.filter(l => l.status === 'trial').length,
    expired: licenses.filter(l => l.status === 'expired').length
  };

  // Filtered licenses
  const filteredLicenses = licenses.filter(license => {
    const org = organizations.find(o => o.id === license.organization);
    const orgName = org ? org.name : '';
    return orgName.toLowerCase().includes(searchTerm.toLowerCase()) ||
           license.license_key?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Get organization name
  const getOrgName = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    return org ? org.name : 'N/A';
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active': return 'active';
      case 'trial': return 'accent';
      case 'expired': return 'danger';
      case 'suspended': return 'inactive';
      default: return 'inactive';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Aktif';
      case 'trial': return 'Deneme';
      case 'expired': return 'Süresi Dolmuş';
      case 'suspended': return 'Askıya Alınmış';
      default: return status;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  const getDaysRemaining = (license) => {
    if (!license.valid_until) return 'N/A';
    const now = new Date();
    const endDate = new Date(license.valid_until);
    const diff = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    if (diff < 0) return 'Süresi Doldu';
    if (diff === 0) return 'Bugün Sonu';
    if (diff === 1) return '1 Gün';
    return `${diff} Gün`;
  };

  const getDaysRemainingClass = (license) => {
    if (!license.valid_until) return '';
    const diff = Math.ceil((new Date(license.valid_until) - new Date()) / (1000 * 60 * 60 * 24));
    if (diff < 0) return 'days-remaining-expired';
    if (diff < 7) return 'days-remaining-urgent';
    return '';
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1>Lisans Yönetimi</h1>
          <p className="page-header-subtitle">Organizasyon lisansları ve geçerlilikleri</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={fetchLicenses}>
            ↻ Yenile
          </button>
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            + Yeni Lisans
          </button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="error-banner">
          <WarningIcon />
          <span>{error}</span>
          <button onClick={() => setError(null)}>Kapat</button>
        </div>
      )}

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <p className="stat-value">{stats.total}</p>
          <h3>Toplam Lisans</h3>
        </div>
        <div className="stat-card stat-card-positive">
          <p className="stat-value">{stats.active}</p>
          <h3>Aktif</h3>
        </div>
        <div className="stat-card stat-card-accent">
          <p className="stat-value">{stats.trial}</p>
          <h3>Deneme</h3>
        </div>
        <div className="stat-card stat-card-danger">
          <p className="stat-value">{stats.expired}</p>
          <h3>Süresi Dolmuş</h3>
        </div>
      </div>

      {/* Table Container */}
      <div className="table-container">
        <div className="table-header">
          <h2>Tüm Lisanslar</h2>
          <div className="search-box">
            <input
              type="text"
              placeholder="Lisans veya organizasyon ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {filteredLicenses.length === 0 ? (
          <div className="empty-state">
            <SettingsIcon />
            <h3>Lisans Bulunamadı</h3>
            <p>Henüz hiç lisans oluşturulmamış</p>
            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
              İlk Lisansı Oluştur
            </button>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Organizasyon</th>
                <th>Lisans Anahtarı</th>
                <th>Durum</th>
                <th>Geçerlilik</th>
                <th>Kalan Süre</th>
                <th>Fiyat</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredLicenses.map(license => (
                <tr key={license.id}>
                  <td>
                    <div className="org-name">
                      <SettingsIcon />
                      {getOrgName(license.organization)}
                    </div>
                  </td>
                  <td>
                    <span className="subdomain-badge">
                      {license.license_key?.substring(0, 20)}...
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeColor(license.status)}`}>
                      <span className="status-indicator"></span>
                      {getStatusLabel(license.status)}
                    </span>
                  </td>
                  <td>
                    {formatDate(license.valid_from)} - {formatDate(license.valid_until)}
                  </td>
                  <td>
                    <strong className={getDaysRemainingClass(license)}>
                      {getDaysRemaining(license)}
                    </strong>
                  </td>
                  <td>
                    {license.price > 0 ? `${license.price} ${license.billing_cycle === 'monthly' ? '/ay' : '/yıl'}` : 'Ücretsiz'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="action-link" onClick={() => openEditModal(license)}>
                        Düzenle
                      </button>
                      <button
                        className="btn-icon delete"
                        onClick={() => openDeleteModal(license)}
                        title="Sil"
                      >
                        <TrashIcon />
                      </button>
                      <button
                        className={`btn btn-toggle btn-${license.status === 'active' ? 'secondary' : 'success'}`}
                        onClick={() => handleToggleStatus(license)}
                      >
                        {license.status === 'active' ? 'Askıya Al' : 'Aktif Et'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Yeni Lisans Oluştur</h2>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateLicense}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Organizasyon *</label>
                  <select
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Seçiniz...</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Durum *</label>
                  <select name="status" value={formData.status} onChange={handleChange}>
                    <option value="trial">Deneme</option>
                    <option value="active">Aktif</option>
                    <option value="suspended">Askıya Alınmış</option>
                    <option value="expired">Süresi Dolmuş</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Başlangıç Tarihi *</label>
                  <input
                    type="date"
                    name="valid_from"
                    value={formData.valid_from}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Bitiş Tarihi *</label>
                  <input
                    type="date"
                    name="valid_until"
                    value={formData.valid_until}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Faturalandırma Döngüsü</label>
                  <select name="billing_cycle" value={formData.billing_cycle} onChange={handleChange}>
                    <option value="monthly">Aylık</option>
                    <option value="yearly">Yıllık</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Fiyat (₺)</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="form-group">
                  <div className="checkbox-wrapper">
                    <input
                      type="checkbox"
                      name="auto_renew"
                      checked={formData.auto_renew}
                      onChange={handleChange}
                      id="auto_renew_create"
                    />
                    <label htmlFor="auto_renew_create">
                      Otomatik yenileme
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  Oluştur
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedLicense && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Lisansı Düzenle</h2>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <form onSubmit={handleUpdateLicense}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Organizasyon *</label>
                  <select
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    required
                    disabled
                  >
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                  <small className="form-group-hint">
                    Organizasyon değiştirilemez
                  </small>
                </div>
                <div className="form-group">
                  <label>Durum *</label>
                  <select name="status" value={formData.status} onChange={handleChange}>
                    <option value="trial">Deneme</option>
                    <option value="active">Aktif</option>
                    <option value="suspended">Askıya Alınmış</option>
                    <option value="expired">Süresi Dolmuş</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Başlangıç Tarihi *</label>
                  <input
                    type="date"
                    name="valid_from"
                    value={formData.valid_from}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Bitiş Tarihi *</label>
                  <input
                    type="date"
                    name="valid_until"
                    value={formData.valid_until}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Faturalandırma Döngüsü</label>
                  <select name="billing_cycle" value={formData.billing_cycle} onChange={handleChange}>
                    <option value="monthly">Aylık</option>
                    <option value="yearly">Yıllık</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Fiyat (₺)</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="form-group">
                  <div className="checkbox-wrapper">
                    <input
                      type="checkbox"
                      name="auto_renew"
                      checked={formData.auto_renew}
                      onChange={handleChange}
                      id="auto_renew_edit"
                    />
                    <label htmlFor="auto_renew_edit">
                      Otomatik yenileme
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  Güncelle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedLicense && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><WarningIcon /> Lisansı Sil</h2>
              <button className="close-btn" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="modal-danger-notice">
                <WarningIcon />
                <p>Bu işlem geri alınamaz!</p>
              </div>
              <p className="modal-delete-summary">
                <strong>{organizations.find(o => o.id === selectedLicense.organization)?.name || 'Bilinmeyen Organizasyon'}</strong> organizasyonunun lisansını silmek üzeresiniz.
              </p>
              <p className="modal-delete-summary">
                Lisans bilgileri:
              </p>
              <ul className="modal-delete-list">
                <li>Durum: <strong>{selectedLicense.status === 'active' ? 'Aktif' : selectedLicense.status === 'trial' ? 'Deneme' : selectedLicense.status === 'expired' ? 'Süresi Dolmuş' : 'Askıya Alınmış'}</strong></li>
                <li>Geçerlilik: <strong>{new Date(selectedLicense.valid_from).toLocaleDateString('tr-TR')} - {new Date(selectedLicense.valid_until).toLocaleDateString('tr-TR')}</strong></li>
                <li>Fiyat: <strong>{selectedLicense.price > 0 ? `${selectedLicense.price} ${selectedLicense.billing_cycle === 'monthly' ? '/ay' : '/yıl'}` : 'Ücretsiz'}</strong></li>
                <li>Otomatik Yenileme: <strong>{selectedLicense.auto_renew ? 'Açık' : 'Kapalı'}</strong></li>
              </ul>
              <p className="modal-delete-confirm">
                Bu lisansı silmek istediğinizden emin misiniz?
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                İptal
              </button>
              <button type="button" className="btn btn-danger" onClick={handleDeleteLicense}>
                <TrashIcon /> Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LicensesPage;
